import type { AxiosProgressEvent } from 'axios';
import { AxiosError } from 'axios';
import { api } from './api';
import { expedienteApi } from './expedienteApi';
import { handleApiError } from '@/utils/errorHandler';
import type {
  ContactExpedienteSummary,
  ContactExpedienteDocument,
  ContactExpedienteTramiteStatus,
} from '@/types/contactExpedienteWidget';

const CRM_CONTACTS_BASE = '/crm/contacts';
const EXPEDIENTES_BASE = '/expedientes';

function normalizeStatus(value: unknown): ContactExpedienteTramiteStatus {
  const allowed: ContactExpedienteTramiteStatus[] = [
    'matriz',
    'new',
    'triage',
    'in_progress',
    'pending_info',
    'lawyer_review',
    'ready_to_submit',
    'submitted',
    'resolved',
    'completed',
    'archived',
    'unknown',
  ];
  if (typeof value === 'string' && (allowed as string[]).includes(value)) {
    return value as ContactExpedienteTramiteStatus;
  }
  return 'unknown';
}

function asSummary(data: unknown, contactId: string): ContactExpedienteSummary {
  const raw = (data ?? {}) as Record<string, unknown>;
  const contractRaw = raw['contrato_vigente'];
  const contractObj =
    contractRaw != null && typeof contractRaw === 'object'
      ? (contractRaw as Record<string, unknown>)
      : null;

  const rawNextAction = raw['next_action_suggested'];
  const nextActionSuggested =
    typeof rawNextAction === 'string' && rawNextAction.trim() !== ''
      ? rawNextAction
      : null;

  return {
    contact_id: String(raw['contact_id'] ?? contactId),
    expediente_id:
      raw['expediente_id'] == null || raw['expediente_id'] === ''
        ? null
        : String(raw['expediente_id']),
    expediente_number: String(raw['expediente_number'] ?? 'Sin numero de expediente'),
    tramite_status: normalizeStatus(raw['tramite_status']),
    contrato_vigente: contractObj
      ? {
          plan_type: String(contractObj['plan_type'] ?? 'Sin plan'),
          status: String(contractObj['status'] ?? 'Sin estado'),
          relevant_date:
            typeof contractObj['relevant_date'] === 'string'
              ? contractObj['relevant_date']
              : null,
          hiring_code:
            typeof contractObj['hiring_code'] === 'string' && contractObj['hiring_code'].trim() !== ''
              ? contractObj['hiring_code'].trim()
              : null,
        }
      : null,
    next_action_suggested: nextActionSuggested,
    is_partial: Boolean(raw['is_partial']),
  };
}

function asDocument(data: unknown, expedienteId: string): ContactExpedienteDocument | null {
  if (data == null || typeof data !== 'object') return null;
  const raw = data as Record<string, unknown>;
  const id = raw['id'];
  const name = raw['name'] ?? raw['nombre'];
  const url = raw['url'];
  if (!id || !name || !url) return null;

  const tipoDoc = raw['tipo_documento'];
  return {
    id: String(id),
    expediente_id: String(raw['expediente_id'] ?? expedienteId),
    name: String(name),
    mime_type: String(raw['mime_type'] ?? raw['tipo'] ?? 'application/octet-stream'),
    size_bytes: Number(raw['size_bytes'] ?? raw['tamaño'] ?? 0),
    url: String(url),
    uploaded_at: typeof raw['uploaded_at'] === 'string' ? raw['uploaded_at'] : null,
    tipo_documento:
      typeof tipoDoc === 'string' && tipoDoc.trim() !== '' ? tipoDoc.trim() : null,
    descripcion:
      typeof raw['descripcion'] === 'string' && (raw['descripcion'] as string).trim() !== ''
        ? (raw['descripcion'] as string).trim()
        : null,
  };
}

function emptySummary(contactId: string): ContactExpedienteSummary {
  return {
    contact_id: contactId,
    expediente_id: null,
    expediente_number: 'Sin numero de expediente',
    tramite_status: 'unknown',
    contrato_vigente: null,
    next_action_suggested: null,
    is_partial: false,
  };
}

async function getContactExpedienteSummary(contactId: string): Promise<ContactExpedienteSummary> {
  try {
    const { data } = await api.get(`${CRM_CONTACTS_BASE}/${contactId}/expediente-summary`);
    return asSummary(data, contactId);
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return emptySummary(contactId);
    }
    throw handleApiError(error);
  }
}

async function getExpedienteDocuments(expedienteId: string): Promise<ContactExpedienteDocument[]> {
  try {
    const { data } = await api.get(`${EXPEDIENTES_BASE}/${expedienteId}/documents`);
    const rows = Array.isArray(data) ? data : Array.isArray((data as { items?: unknown[] })?.items) ? (data as { items: unknown[] }).items : [];
    return rows
      .map((row) => asDocument(row, expedienteId))
      .filter((row): row is ContactExpedienteDocument => row != null);
  } catch (error) {
    try {
      const fallback = await expedienteApi.getById(expedienteId);
      const archivos = Array.isArray(fallback.archivos) ? fallback.archivos : [];
      return archivos
        .map((archivo) =>
          asDocument(
            {
              id: archivo.id,
              expediente_id: archivo.expediente_id,
              nombre: archivo.nombre,
              tipo: archivo.tipo,
              tamaño: archivo.tamaño,
              url: archivo.url,
              uploaded_at: archivo.uploaded_at,
            },
            expedienteId
          )
        )
        .filter((row): row is ContactExpedienteDocument => row != null);
    } catch (fallbackError) {
      throw handleApiError(fallbackError);
    }
  }
}

export interface ExpedienteArchivoUpdate {
  nombre?: string;
  descripcion?: string;
  tipo_documento?: string;
}

async function uploadExpedienteDocument(
  expedienteId: string,
  file: File,
  onUploadProgress?: (event: AxiosProgressEvent) => void,
  tipoDocumento?: string | null
): Promise<ContactExpedienteDocument> {
  const formData = new FormData();
  formData.append('file', file);
  if (tipoDocumento != null && tipoDocumento.trim() !== '') {
    formData.append('tipo_documento', tipoDocumento.trim());
  }
  try {
    const { data } = await api.post(`${EXPEDIENTES_BASE}/${expedienteId}/documents`, formData, {
      onUploadProgress,
    });
    const mapped = asDocument(data, expedienteId);
    if (!mapped) {
      throw new Error('Respuesta de upload invalida');
    }
    return mapped;
  } catch (error) {
    try {
      const fallback = await api.post(`${EXPEDIENTES_BASE}/${expedienteId}/archivos`, formData, {
        onUploadProgress,
      });
      const mapped = asDocument(fallback.data, expedienteId);
      if (!mapped) {
        throw new Error('Respuesta de upload fallback invalida');
      }
      return mapped;
    } catch (fallbackError) {
      throw handleApiError(fallbackError);
    }
  }
}

async function updateExpedienteDocument(
  expedienteId: string,
  documentId: string,
  body: ExpedienteArchivoUpdate
): Promise<ContactExpedienteDocument> {
  const { data } = await api.patch(
    `${EXPEDIENTES_BASE}/${expedienteId}/documents/${documentId}`,
    body
  );
  const mapped = asDocument(data, expedienteId);
  if (!mapped) {
    throw new Error('Respuesta de actualización de documento inválida');
  }
  return mapped;
}

async function deleteExpedienteDocument(expedienteId: string, documentId: string): Promise<void> {
  try {
    await api.delete(`${EXPEDIENTES_BASE}/${expedienteId}/documents/${documentId}`);
  } catch (error) {
    try {
      await api.delete(`${EXPEDIENTES_BASE}/${expedienteId}/archivos/${documentId}`);
    } catch (fallbackError) {
      throw handleApiError(fallbackError);
    }
  }
}

export const contactExpedienteWidgetService = {
  getContactExpedienteSummary,
  getExpedienteDocuments,
  uploadExpedienteDocument,
  updateExpedienteDocument,
  deleteExpedienteDocument,
};

